// /app/frontend/src/pages/seo/AiMatchmakingAppPage.jsx
// Focus: Honest "tech serves humans" framing — what AI does and doesn't do.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function AiMatchmakingAppPage() {
  const path = '/ai-matchmaking-app';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'AI Matchmaking App', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="AI Matchmaking App For Meaningful Connections"
        description="Discover how AI-assisted matching helps people find compatible relationships, shared values, and deeper connections."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Approach"
        headline="Technology Meets Human Connection"
        sub="AI helps you find people you might miss. The relationship itself is still yours to build."
        body="Honest framing of what AI-assisted matching does — and what it does not — at UnityLove."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Why Intentional Dating?', to: '/dating-app-for-intentional-relationships' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* What AI matchmaking actually is */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            What AI Matchmaking Actually Is
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              &ldquo;AI matchmaking&rdquo; sounds like a robot finds you a soulmate. It is not that. What AI actually does on a thoughtfully built dating platform is far more modest — and far more useful — than the marketing usually suggests.
            </p>
            <p>
              At its best, AI-assisted matching looks at the things you have said you care about (values, family vision, relationship style, lifestyle preferences) and surfaces people whose answers genuinely overlap. It catches profiles you might miss in a sea of swiping. It learns over time which kinds of people you actually engage with, not just the ones you click on. And it tries to balance the chemistry of profile attraction with the deeper compatibility of shared values.
            </p>
          </div>
        </section>

        {/* What it does */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            What AI Does Well
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🎯"
              accent="rose"
              title="Surfaces Compatible Profiles"
              body="AI weighs hundreds of profile signals at once. It can spot strong matches based on values and life vision that you might scroll past on photos alone."
            />
            <FeatureCard
              icon="🧠"
              accent="emerald"
              title="Learns Your Real Preferences"
              body="Not just who you swipe right on, but who you actually have meaningful conversations with. The system gets smarter over time about the kinds of people you connect with."
            />
            <FeatureCard
              icon="🔍"
              accent="sky"
              title="Catches Profiles You Would Miss"
              body="Manual searching has limits. AI can find compatible members across the platform you would never reach by browsing."
            />
            <FeatureCard
              icon="⚖️"
              accent="amber"
              title="Balances Many Dimensions"
              body="Faith, family vision, relationship style, lifestyle, geography — AI weighs them all together instead of forcing you to choose between filters."
            />
            <FeatureCard
              icon="💬"
              accent="violet"
              title="Suggests Conversation Starters"
              body="Optional AI assistance can help craft thoughtful first messages — but never sends them for you. The voice that lands in someone&rsquo;s inbox is always yours."
            />
            <FeatureCard
              icon="🛡️"
              accent="emerald"
              title="Flags Risky Behavior"
              body="AI helps moderation catch harassment patterns, fake profiles, and bot networks before they reach members — protecting community safety without invasive surveillance."
            />
          </div>
        </section>

        {/* What it doesn't do */}
        <section className="mb-14 rounded-2xl border border-amber-200 bg-amber-50 p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-amber-900 mb-4">
            What AI Does Not Do
          </h2>
          <ul className="space-y-3 text-amber-900">
            <li><strong className="text-amber-900">It does not find your &ldquo;soulmate.&rdquo;</strong> There is no such algorithm. Compatibility is real; love is built.</li>
            <li><strong className="text-amber-900">It does not replace conversation.</strong> A high match score is a starting hypothesis, not a prediction. You still have to talk.</li>
            <li><strong className="text-amber-900">It does not pretend to be a person.</strong> Conversations on UnityLove are between real, verified members. AI never impersonates a profile.</li>
            <li><strong className="text-amber-900">It does not date for you.</strong> Showing up, being honest, asking the right questions, and being kind are still entirely on you.</li>
            <li><strong className="text-amber-900">It does not work without good profiles.</strong> The richer your profile, the better the matches. AI cannot read minds — only the words you give it.</li>
          </ul>
        </section>

        {/* Privacy framing */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            How We Treat Your Data
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              The AI features at UnityLove run on the data you actively provide — profile fields, prompts, in-app behavior. We do not sell member data. We do not train external models on private conversations. The system is here to help you connect, not to extract you.
            </p>
            <p>
              You can also turn off optional AI-assisted features in your settings if you prefer to navigate the platform manually. The core experience never requires AI participation.
            </p>
          </div>
        </section>

        {/* Practical advice */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            How To Get Better Matches
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <ul className="list-disc list-inside ml-2 space-y-1.5">
              <li>Fill out the long-form profile fields completely. Specific answers beat vague ones.</li>
              <li>Be honest about your timeline — short-term, long-term, family-building, exploring.</li>
              <li>Engage with profiles that interest you. The system learns from your actual conversations, not your swipes.</li>
              <li>Update your profile as you change. The version of you from a year ago should not be matching anyone today.</li>
              <li>Use the prompts. People who answer prompts get matched with people who also answer prompts — usually a higher-quality conversation cohort.</li>
            </ul>
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/dating-app-for-intentional-relationships" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating App For Intentional Relationships</p>
              <p className="text-sm text-slate-600 mt-1">Why slow dating works better.</p>
            </a>
            <a href="/verified-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Verified Dating App</p>
              <p className="text-sm text-slate-600 mt-1">How verification creates safer connections.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
